import { Theme } from "@/components/theme/themeProvider"
import type { UserCellVariants } from "@/components/ui/layout/table/userCell"
import { StripeErrorCode } from "@/modules/finances/types/stripeErrorTypes"
import { word } from "@modules/core/utils/i18n"

import { OutOfBandTransactionStatus } from "@modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { TransactionEntity } from "@modules/finances/entities/transactionEntity"
import { PaymentMethodType, TransactionStatus } from "@modules/finances/types/financesType"
import { isInArray } from "@modules/utils/isInArray"
import dayjs from "dayjs"

export function getTransactionColorStatus(
  transaction: TransactionDetailsEntity | TransactionEntity,
  theme: Theme,
): UserCellVariants {
  switch (transaction.status) {
    case TransactionStatus.SUCCEEDED:
      return "green"
    case TransactionStatus.PENDING:
      return (transaction instanceof TransactionEntity &&
        transaction.paymentMethodType === PaymentMethodType.SEPA_DEBIT) ||
        (transaction instanceof TransactionDetailsEntity &&
          transaction.paymentMethodDetail?.type === PaymentMethodType.SEPA_DEBIT)
        ? "green"
        : "yellow"
    case TransactionStatus.FAILED:
      return "red"
    case TransactionStatus.DISPUTED:
      return "red"
    case TransactionStatus.REFUNDED:
      return theme === Theme.OwnerDark ? "white" : "black"
  }
}

export function getTransactionStatusWording(
  transaction: TransactionDetailsEntity | TransactionEntity,
): string | undefined {
  if (transaction.outOfBandStatus) {
    return getOutOfBandTransactionStatusWording(transaction)
  }

  const wordStatus: { [key in TransactionStatus]: string | undefined } = {
    SUCCEEDED: word("finances.page.table.status.succeded", { count: 1 }),
    PENDING: word("finances.page.table.status.pending", { count: 1 }),
    FAILED: word("finances.page.table.status.failed", { count: 1 }),
    DISPUTED: word("finances.page.table.status.disputed", { count: 1 }),
    REFUNDED: word("finances.page.table.status.refunded", { count: 1 }),
    ABANDONED: word("finances.page.table.status.abandoned", { count: 1 }),
  }

  if (transaction.refunded) return word("finances.page.table.status.refunded", { count: 1 })
  if (transaction.status === TransactionStatus.PENDING)
    if (
      (transaction instanceof TransactionEntity && transaction.paymentMethodType === PaymentMethodType.SEPA_DEBIT) ||
      (transaction instanceof TransactionDetailsEntity &&
        transaction.paymentMethodDetail?.type === PaymentMethodType.SEPA_DEBIT)
    ) {
      return word("finances.page.table.status.pendingSepa")
    } else {
      return word("finances.page.table.status.pendingByCard")
    }

  return wordStatus[transaction.status]
}

export function getTransactionStatusDetailsWording(transaction: TransactionEntity): string | undefined {
  if (transaction.paidAt && transaction.status !== TransactionStatus.DISPUTED) {
    return dayjs(transaction.paidAt).format("L")
  }
  if (transaction.status === TransactionStatus.FAILED && transaction.paymentError?.errorCode) {
    return getTransactionErrorReasonWording(transaction.paymentError.errorCode, true)
  }
  if (transaction.status === TransactionStatus.PENDING && !!transaction.outOfBandStatus) {
    return word("finances.details.status.pendingForPaymentAtCounter.label")
  }
  if (transaction.status === TransactionStatus.PENDING && transaction.paymentMethodType === PaymentMethodType.CARD) {
    return word("finances.details.status.pendingFor3DSecure.label")
  }
  return undefined
}

export function getTransactionStatusDetailsTooltipContent(transaction: TransactionEntity): string | undefined {
  if (transaction.payoutAt) {
    return word("finances.page.table.status.tooltip.paidOut", {
      date: dayjs(transaction.paidAt).format("L"),
      payout_date: dayjs(transaction.payoutAt).format("L"),
    })
  } else if (
    transaction.outOfBandStatus === OutOfBandTransactionStatus.CARD ||
    transaction.outOfBandStatus === OutOfBandTransactionStatus.CASH ||
    transaction.outOfBandStatus === OutOfBandTransactionStatus.CHECK
  ) {
    return word("finances.page.table.status.tooltip.paidAtCounter", { date: dayjs(transaction.date).format("L") })
  } else if (transaction.status === TransactionStatus.SUCCEEDED) {
    return word("finances.page.table.status.tooltip.succeded", { date: dayjs(transaction.paidAt).format("L") })
  } else if (transaction.status === TransactionStatus.FAILED && transaction.paymentError?.errorCode) {
    return word("finances.page.table.status.tooltip.failed", {
      reason: getTransactionErrorReasonWording(transaction.paymentError.errorCode),
      action: getTransactionErrorActionWording(transaction.paymentError.errorCode),
      last_attempt_date: dayjs(transaction.paymentError.date).format("L"),
    })
  } else if (transaction.status === TransactionStatus.DISPUTED) {
    return word("finances.page.table.status.tooltip.disputed", {
      status_update_date: dayjs(transaction.date).format("L"),
    })
  } else if (transaction.outOfBandStatus) {
    return word("finances.page.table.status.tooltip.pendingForPaymentAtCounter", {
      invoice_creation_date: dayjs(transaction.date).format("L"),
    })
  } else if (transaction.status === TransactionStatus.PENDING && !!transaction.outOfBandStatus) {
    return word("finances.page.table.status.tooltip.pendingForPaymentAtCounter", {
      date: dayjs(transaction.date).format("L"),
    })
  } else if (
    transaction.status === TransactionStatus.PENDING &&
    transaction.paymentMethodType === PaymentMethodType.CARD
  ) {
    return word("finances.page.table.status.tooltip.pendingFor3DSecure", {
      last_attempt_date: transaction.paymentError?.date
        ? dayjs(transaction.paymentError.date).format("L")
        : dayjs(transaction.date).format("L"),
    })
  }
  return undefined
}

export function getTransactionColorStatusDetails(transaction: TransactionEntity): UserCellVariants {
  if (transaction.status === TransactionStatus.FAILED) {
    return "red"
  }
  return "default"
}

export function getTransactionHistoryStatusWording(status: TransactionStatus): string | undefined {
  const wordStatus: { [key in TransactionStatus]: string | undefined } = {
    SUCCEEDED: word("finances.page.table.status.succeded", { count: 1 }),
    PENDING: word("finances.page.table.status.pending", { count: 1 }),
    FAILED: word("finances.page.table.status.failed", { count: 1 }),
    DISPUTED: word("finances.page.table.status.disputed", { count: 1 }),
    REFUNDED: word("finances.page.table.status.refunded", { count: 1 }),
    ABANDONED: word("finances.page.table.status.abandoned", { count: 1 }),
  }
  return wordStatus[status]
}

export function getTransactionHistoryColorStatus(status: TransactionStatus): string {
  switch (status) {
    case TransactionStatus.FAILED:
    case TransactionStatus.DISPUTED:
      return "text-red-500"
    default:
      return "text-textDefault-body"
  }
}

export function getOutOfBandTransactionStatusWording(
  transaction: TransactionDetailsEntity | TransactionEntity,
): string | undefined {
  if (transaction.outOfBandStatus === OutOfBandTransactionStatus.PENDING_VALIDATION) {
    return word("finances.page.table.outOfBand.status.pendingValidation")
  }
  if (
    isInArray(
      [OutOfBandTransactionStatus.PAST_DUE, OutOfBandTransactionStatus.HIDDEN_PAST_DUE],
      transaction.outOfBandStatus,
    )
  ) {
    return word("finances.page.table.outOfBand.status.pastDue", {
      count: dayjs().diff(dayjs(transaction.date), "day"),
    })
  }
  if (
    isInArray(
      [
        OutOfBandTransactionStatus.PAID,
        OutOfBandTransactionStatus.CARD,
        OutOfBandTransactionStatus.CASH,
        OutOfBandTransactionStatus.CHECK,
      ],
      transaction.outOfBandStatus,
    )
  ) {
    return word("finances.page.table.outOfBand.status.validated")
  }

  return undefined
}

export function getOutOfBandTransactionStatusShortWording(transaction: TransactionDetailsEntity): string | undefined {
  if (transaction.outOfBandStatus === OutOfBandTransactionStatus.PENDING_VALIDATION)
    return word("finances.page.table.outOfBand.status.pendingValidationShort")

  if (
    isInArray(
      [OutOfBandTransactionStatus.PAST_DUE, OutOfBandTransactionStatus.HIDDEN_PAST_DUE],
      transaction.outOfBandStatus,
    )
  )
    return word("finances.page.table.outOfBand.status.pastDueShort")

  return undefined
}

export function getOutOfBandTransactionColorStatus(transaction: TransactionDetailsEntity): UserCellVariants {
  switch (transaction.outOfBandStatus) {
    case OutOfBandTransactionStatus.PENDING_VALIDATION:
      return "yellow"
    case OutOfBandTransactionStatus.PAST_DUE:
    case OutOfBandTransactionStatus.HIDDEN_PAST_DUE:
      return "red"
    case OutOfBandTransactionStatus.PAID:
    case OutOfBandTransactionStatus.CARD:
    case OutOfBandTransactionStatus.CHECK:
    case OutOfBandTransactionStatus.CASH:
      return "green"
    default:
      return "neutral"
  }
}

export function getTransactionErrorReasonWording(error: StripeErrorCode, isShort: boolean = false): string | undefined {
  switch (error) {
    case StripeErrorCode.CHARGE_EXCEEDS_SOURCE_LIMIT:
      return isShort
        ? word("finances.details.status.chargeExceedsSourceLimit.labelShort")
        : word("finances.details.status.chargeExceedsSourceLimit.label")
    case StripeErrorCode.EXPIRED_CARD:
      return isShort
        ? word("finances.details.status.expiredCard.labelShort")
        : word("finances.details.status.expiredCard.label")
    case StripeErrorCode.INCORRECT_NUMBER:
      return isShort
        ? word("finances.details.status.incorrectNumber.labelShort")
        : word("finances.details.status.incorrectNumber.label")
    case StripeErrorCode.CARD_DECLINED:
      return isShort
        ? word("finances.details.status.cardDeclined.labelShort")
        : word("finances.details.status.cardDeclined.label")
    case StripeErrorCode.INSUFFICIENT_FUNDS:
      return isShort
        ? word("finances.details.status.insufficientFunds.labelShort")
        : word("finances.details.status.insufficientFunds.label")
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return isShort
        ? word("finances.details.status.authenticationRequired.labelShort")
        : word("finances.details.status.authenticationRequired.label")
    default:
      return isShort
        ? word("finances.details.status.unknown.labelShort")
        : word("finances.details.status.unknown.label")
  }
}

export function getTransactionErrorActionWording(error: StripeErrorCode): string | undefined {
  switch (error) {
    case StripeErrorCode.CHARGE_EXCEEDS_SOURCE_LIMIT:
      return word("finances.details.status.chargeExceedsSourceLimit.action")
    case StripeErrorCode.EXPIRED_CARD:
      return word("finances.details.status.expiredCard.action")
    case StripeErrorCode.INCORRECT_NUMBER:
      return word("finances.details.status.incorrectNumber.action")
    case StripeErrorCode.CARD_DECLINED:
      return word("finances.details.status.cardDeclined.action")
    case StripeErrorCode.INSUFFICIENT_FUNDS:
      return word("finances.details.status.insufficientFunds.action")
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return word("finances.details.status.authenticationRequired.action")
    default:
      return word("finances.details.status.unknown.action")
  }
}

export function getTransactionErrorAction(error: StripeErrorCode): "contactAthlete" | "contactSupport" | undefined {
  switch (error) {
    case StripeErrorCode.CHARGE_EXCEEDS_SOURCE_LIMIT:
      return "contactSupport"
    case StripeErrorCode.UNKNOWN:
      return "contactSupport"
    case StripeErrorCode.EXPIRED_CARD:
      return "contactAthlete"
    case StripeErrorCode.INCORRECT_NUMBER:
      return "contactAthlete"
    case StripeErrorCode.CARD_DECLINED:
      return "contactAthlete"
    case StripeErrorCode.INSUFFICIENT_FUNDS:
      return "contactAthlete"
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return "contactAthlete"
    default:
      return undefined
  }
}
